import React from 'react';
import { graphql, Link } from 'gatsby';
import Helmet from 'react-helmet';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const InformationArticle = ({ data }) => {
  const { frontmatter, html } = data.markdownRemark;
  const { title, summary, banner, faq, path, date, keywords } = frontmatter;
  const siteUrl = 'https://www.mantas.cn';

  // 相关阅读：站内其他 internal 文章（过滤当前文章，最多 2 篇）
  const relatedEdges = (data.allInformationArticlesJson
    ? data.allInformationArticlesJson.edges
    : []
  )
    .map(({ node }) => node)
    .filter(node => node.type === 'internal' && node.slug && node.slug !== path)
    .slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: summary,
    image: `${siteUrl}${banner}`,
    url: `${siteUrl}${path}`,
    datePublished: date,
    dateModified: date,
    author: {
      '@type': 'Organization',
      name: '魔鬼鱼订货平台',
      url: siteUrl
    },
    publisher: {
      '@type': 'Organization',
      name: '魔鬼鱼订货平台',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/og-default.png`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${path}`
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '信息发布',
        item: `${siteUrl}/information`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: `${siteUrl}${path}`
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (faq || []).map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return (
    <Layout bodyClass="page-information-article">
      <SEO
        title={`${title} - 魔鬼鱼订货平台`}
        description={summary}
        path={path}
        ogImage={banner}
      />
      <Helmet>
        {keywords && keywords.length > 0 && (
          <meta name="keywords" content={keywords.join(',')} key="keywords" />
        )}
        <script type="application/ld+json" key="article">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json" key="breadcrumb">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {faq && faq.length > 0 && (
          <script type="application/ld+json" key="faq">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <section className="information-article-detail">
        <div className="information-article-container">
          {/* Banner */}
          <div className="information-article-banner">
            <img
              src={banner}
              alt={title}
              className="information-article-banner-image"
            />
          </div>

          {/* Content */}
          <article className="information-article-body">
            <h1 className="information-article-heading">{title}</h1>
            {date && (
              <p className="information-article-date">
                发布于 <time dateTime={date}>{date}</time>
                <span className="information-article-date-author"> · 魔鬼鱼订货平台</span>
              </p>
            )}
            {keywords && keywords.length > 0 && (
              <div className="information-article-tags" aria-label="关键词标签">
                {keywords.map((keyword, index) => (
                  <span key={index} className="information-article-tag">
                    {keyword}
                  </span>
                ))}
              </div>
            )}
            <div
              className="information-article-content"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </article>

          {/* FAQ Section */}
          {faq && faq.length > 0 && (
            <section className="information-article-faq" aria-label="常见问题">
              <h2 className="information-article-faq-title">常见问题</h2>
              <div className="information-article-faq-list">
                {faq.map((item, index) => (
                  <div key={index} className="information-article-faq-item">
                    <h3 className="information-article-faq-question">{item.question}</h3>
                    <p className="information-article-faq-answer">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Related Reading */}
          {relatedEdges.length > 0 && (
            <section className="information-article-related" aria-label="相关阅读">
              <h2 className="information-article-related-title">相关阅读</h2>
              <div className="information-article-related-list">
                {relatedEdges.map(article => (
                  <Link
                    key={article.slug}
                    to={article.slug}
                    className="information-article-related-card"
                  >
                    <div className="information-article-related-image-wrapper">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="information-article-related-image"
                        loading="lazy"
                      />
                    </div>
                    <div className="information-article-related-content">
                      <h3 className="information-article-related-card-title">
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="information-article-related-summary">
                          {article.summary}
                        </p>
                      )}
                      <span className="information-article-related-more">阅读全文 ›</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Back Button */}
          <div className="information-article-back">
            <Link to="/information" className="information-article-back-btn">
              ← 返回信息发布
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export const query = graphql`
  query($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        path
        summary
        banner
        date
        keywords
        faq {
          question
          answer
        }
      }
      html
    }
    allInformationArticlesJson {
      edges {
        node {
          title
          slug
          image
          type
          summary
        }
      }
    }
  }
`;

export default InformationArticle;